import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * ConfirmDialog - A modal confirmation dialog for Gigabot
 *
 * Replaces window.confirm() with a styled modal that matches the Gigabot design.
 */
export const ConfirmDialog = ({
  isOpen,
  title = 'Confirm',
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  variant = 'danger',
}) => {
  const dialogRef = useRef(null);

  // Focus the dialog when it opens for keyboard accessibility
  useEffect(() => {
    if (isOpen && dialogRef.current) {
      dialogRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <div
      ref={dialogRef}
      className='confirm-dialog-backdrop'
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      role='dialog'
      aria-modal='true'
      aria-labelledby='confirm-dialog-title'
      aria-describedby='confirm-dialog-message'
      tabIndex={-1}
    >
      <div className='confirm-dialog'>
        <h3 id='confirm-dialog-title' className='confirm-dialog__title'>
          {title}
        </h3>
        <p id='confirm-dialog-message' className='confirm-dialog__message'>
          {message}
        </p>
        <div className='confirm-dialog__actions'>
          <button
            type='button'
            className='confirm-dialog__btn confirm-dialog__btn--cancel'
            onClick={onCancel}
          >
            {cancelText}
          </button>
          <button
            type='button'
            className={`confirm-dialog__btn confirm-dialog__btn--confirm confirm-dialog__btn--${variant}`}
            onClick={onConfirm}
            autoFocus
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

ConfirmDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string,
  message: PropTypes.string.isRequired,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['danger', 'warning', 'primary']),
};

export default ConfirmDialog;
